import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { ZInput, ZTextarea, ZSelect } from "@/components/ZInput";
import ZButton from "@/components/ZButton";
import { CURRENCIES } from "@/lib/format";
import { Plus, Trash2, Gift } from "lucide-react";

export default function OrgVoucherCreatePage() {
  const nav = useNavigate();
  const [type, setType] = useState("amount");
  const [form, setForm] = useState({
    title: "", description: "", quantity: 1, amount: 50000, currency: "GNF", expires_at: "", image_url: "",
  });
  const [items, setItems] = useState([{ label: "", quantity: 1 }]);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.title.trim()) return setErr("Titre obligatoire.");
    if (type === "amount" && parseFloat(form.amount) <= 0) return setErr("Montant doit être positif.");
    const validItems = items.filter((it) => it.label.trim() && it.quantity > 0);
    if (type === "items" && validItems.length === 0) return setErr("Ajoutez au moins un article.");
    setLoading(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        type,
        quantity: parseInt(form.quantity) || 1,
        amount: type === "amount" ? parseFloat(form.amount) : 0,
        currency: form.currency,
        items: type === "items" ? validItems.map((it) => ({ label: it.label.trim(), quantity: parseInt(it.quantity) || 1 })) : [],
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
        image_url: form.image_url,
      };
      const r = await api.post("/vouchers/batches", payload);
      nav(`/org/voucher-batch/${r.data.id}`);
    } catch (e) {
      setErr(errMsg(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="z-container" style={{ maxWidth: 640 }} data-testid="org-voucher-create">
      <h1 className="z-page-title">Nouveau lot de bons d'achat</h1>
      <p className="z-page-sub">Générez un lot de bons (codes uniques) à distribuer.</p>

      <div style={{ display: "flex", gap: 8, marginBottom: 18 }}>
        <button type="button" onClick={() => setType("amount")} className={`z-chip ${type === "amount" ? "z-chip-active" : ""}`} data-testid="voucher-type-amount">💰 Montant</button>
        <button type="button" onClick={() => setType("items")} className={`z-chip ${type === "items" ? "z-chip-active" : ""}`} data-testid="voucher-type-items">🎁 Articles</button>
      </div>

      <form onSubmit={submit} className="z-card" style={{ padding: 24 }}>
        <ZInput label="Titre" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Bons CE Décembre 2026" required data-testid="vc-title" />
        <ZTextarea label="Description" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Valables au Café Plaza, du 01/12 au 31/12" data-testid="vc-description" />

        {type === "amount" ? (
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
            <ZInput label="Montant par bon" type="number" min="1" value={form.amount} onChange={(e) => set("amount", e.target.value)} required data-testid="vc-amount" />
            <ZSelect label="Devise" value={form.currency} onChange={(e) => set("currency", e.target.value)} data-testid="vc-currency">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
            </ZSelect>
          </div>
        ) : (
          <>
            <label className="z-label">Articles inclus dans chaque bon</label>
            {items.map((it, i) => (
              <div key={i} style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                <input className="z-input" value={it.label} onChange={(e) => {
                  const a = [...items]; a[i] = { ...a[i], label: e.target.value }; setItems(a);
                }} placeholder="Ex: Croissant" data-testid={`vc-item-label-${i}`} style={{ flex: 2 }} />
                <input className="z-input" type="number" min="1" value={it.quantity} onChange={(e) => {
                  const a = [...items]; a[i] = { ...a[i], quantity: e.target.value }; setItems(a);
                }} data-testid={`vc-item-qty-${i}`} style={{ flex: 1, maxWidth: 80 }} />
                {items.length > 1 && (
                  <button type="button" onClick={() => setItems(items.filter((_, x) => x !== i))} className="z-btn z-btn-ghost" style={{ color: "var(--z-error)" }} data-testid={`vc-item-remove-${i}`}><Trash2 size={16} /></button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => setItems([...items, { label: "", quantity: 1 }])} className="z-btn z-btn-outline z-btn-sm" data-testid="vc-add-item"><Plus size={14} /> Ajouter un article</button>
            <div style={{ marginBottom: 14 }} />
          </>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <ZInput label="Nombre de bons à générer" type="number" min="1" max="10000" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} required data-testid="vc-quantity" />
          <ZInput label="Date d'expiration (optionnel)" type="datetime-local" value={form.expires_at} onChange={(e) => set("expires_at", e.target.value)} data-testid="vc-expires" />
        </div>

        <ZInput label="URL image (optionnel)" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://..." data-testid="vc-image" />

        {err && <p className="z-error-text" data-testid="vc-error">{err}</p>}

        <ZButton type="submit" block loading={loading} data-testid="vc-submit">
          <Gift size={16} /> Générer le lot
        </ZButton>
      </form>
    </div>
  );
}
